import axios from 'axios';
import Promise from 'bluebird';
import _ from 'lodash';

// Services
import Ticket from 'services/data/ticket';
import Email from 'services/data/email';
import TimeReport from 'services/data/time-report';
import PurchaseOrder from 'services/data/purchase-order';
import File from 'services/data/file';
import Triage from 'services/data/triage';
import { alerts } from 'modules/alerts/actions';

// Actions
import * as actions from 'routes/ticket/store/actions';

const cancellable = {};

export function loadTicket(id, options = {}) {
  cancelLoadTicket();
  cancellable.loadTicket = Ticket.getCancelToken();

  if (options.fetching) actions.set({ fetching: true });

  const load = {
    agreement: null,
    mergedTo: null,
    'agreement.rates': null,
    folder: null,
    closedBy: null,
    tasks: { status: 'Active' },
    'tasks.assignedTo': null,
    'tasks.assignedTo.team': null,
    'tasks.checklist': null,
    'createdBy.team': null,
    company: null,
    workOrder: null
  };

  return Promise.resolve()
    .then(() => Ticket.findOne(id, { load }, { cancelToken: cancellable.loadTicket.token }))
    .tap(() => { delete cancellable.loadTicket; })
    .then((loaded) => {
      const ticket = options.merge ? options.merge(loaded) : loaded;

      actions.set({ ticket, fetching: false });

      return ticket;
    })
    .catch(axios.isCancel, _.noop)
    .catch(alerts.catchError);
}

export function loadTasks(ticket) {
  if (!ticket || !ticket.id) return;

  actions.set({ fetchingTasks: true });

  const load = {
    tasks: { status: { not: null } },
    'tasks.assignedTo': null,
    'tasks.assignedTo.team': null,
    'tasks.checklist': null
  };

  return Ticket.findOne(ticket.id, { load })
    .then((loadedTicket) => {
      actions.updateSingleTicket({ tasks: loadedTicket.tasks });
      actions.set({ fetchingTasks: false });
    });
}

export function loadTriage(ticket) {
  if (!ticket || !ticket.id) return;

  actions.set({ fetchingTriage: true });

  const load = [
    'fromPerson',
    'company',
    'claimedBy.team',
    'ticket',
    'userOnCall.team',
    'lastUserOnCall',
    'transferringTo',
    'recording',
    'replyUser'
  ];

  return Triage.find({ load, limit: 10000, where: { ticket: ticket.id } })
    .then((triageItems) => {
      actions.set({ fetchingTriage: false, triageItems });
    });
}

export function loadFolder(ticket) {
  if (!ticket.folder) return;

  actions.set({ fetchingFiles: true });

  return File.list(ticket.folder.id)
    .then((files) => {
      actions.set({ files, fetchingFiles: false });
    });
}

export function loadEmails(ticket) {
  if (!ticket || !ticket.id) return;

  actions.set({ fetchingEmails: true });

  const load = ['fromPerson.team', 'attachments'];

  return Email.find({ load, where: { ticket: ticket.id } })
    .then((emails) => {
      actions.set({ emails, fetchingEmails: false });
    });
}

export function loadTimeReports(ticket, options = {}) {
  if (!ticket || !ticket.id) return;

  actions.set({ fetchingTimeReports: true });

  const load = ['agreement.rates', 'rate', 'ticket.agreement'];

  return TimeReport.find({ load, where: { ticket: ticket.id } })
    .then((loadedTimeReports) => {
      const timeReports = options.merge ? options.merge(loadedTimeReports) : loadedTimeReports;

      actions.set({ timeReports, fetchingTimeReports: false });
    });
}

export function loadPurchaseOrders(ticket) {
  if (!ticket || !ticket.id) return;

  actions.set({ fetchingPurchaseorders: true });

  const load = [
    'ticket.agreement',
    'agreement',
    'company',
    'createdBy',
    'lineItems',
    'lineItems.expenseBillLineItems',
    'expenseBills.lineItems.purchaseOrderLineItem',
    'expenseBills.receipt',
    'expenseBills.agreement',
    'expenseBills.ticket.agreement'
  ];

  return PurchaseOrder.find({ load, where: { ticket: ticket.id } })
    .then((purchaseOrders) => {
      actions.set({ purchaseOrders, fetchingPurchaseorders: false });
    });
}

export const debouncedLoadTicket = _.debounce((id, options) => {
  loadTicket(id, options)
    .tap(ticket => Promise.all([
      loadTasks(ticket),
      loadTimeReports(ticket),
      loadPurchaseOrders(ticket)
    ]));
}, 200, { maxWait: 500 });

export function cancelLoadTicket() {
  if (_.get(cancellable, 'loadTicket.cancel')) cancellable.loadTicket.cancel();
}



// WEBPACK FOOTER //
// ./app/routes/ticket/utils/load-ticket.js